/**
 * Per-item-kind icon tile accent — reuses the app's existing identity-accent
 * system (`components/l10/l10-theme.ts` `ACCENTS`), the same family the L10
 * feed glyphs and to-do marks draw from, rather than inventing new colors
 * (styleguide `references/kory-marks.md`). A thin system-fact line keeps its
 * bare 14px icon; only a conversation CARD gets a tinted 28px tile.
 */
import { ACCENTS, type AccentName } from "@/components/l10/l10-theme";

export type ActivityCardKind = "email" | "sms" | "call" | "voicemail" | "note" | "scheduled-visit";

/** The shipped mapping (John Lee, 2026-10-29, third pass). The tile marks the item's KIND,
 *  never its outcome: `call` is `emerald` — the phone color — not `rose`, which read as "not
 *  answered" since rose/red carries a problem connotation everywhere else in the app; the
 *  "Answered"/"No answer" word on the row is what says the outcome. `olive`, `forest` and
 *  `brown` are out — all read as gray. `teal` and `sky` are new `ACCENTS` entries (added here,
 *  same fg/soft/border/solid shape as every other entry — no bespoke colors). Missed/no-answer
 *  calls stay bare thin lines with no tile at all (`OneLineRow`), so the "outcome never reads
 *  as a tile color" rule has nothing to contradict there either. */
export const CARD_ACCENT: Record<ActivityCardKind, AccentName> = {
  email: "blue",
  sms: "teal",
  call: "emerald",
  voicemail: "violet",
  note: "amber",
  "scheduled-visit": "sky",
};

export function cardAccent(kind: ActivityCardKind) {
  return ACCENTS[CARD_ACCENT[kind]];
}

/** The tile's fill and glyph color for a conversation kind. */
export function tileTone(kind: ActivityCardKind): string {
  const a = cardAccent(kind);
  return `${a.soft} ${a.fg}`;
}
